import { ChevronRightIcon } from "@chakra-ui/icons";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    NodeState,
    selectControlPanelStatus,
    selectSelectedControlPanelComponent,
    setControlPanelStatus
} from "../../store/slices/node.slice";
import { ControlPanelComponentType } from "../../types/tools";
import AgencyToolControlPanel from "./AgencyToolControlPanel";
import CellEncoderControlPanel from "./CellEncoderControlPanel";
import MemoriesControlPanel from "./MemoriesControlPanel";
import NeuralPathwaysControlPanel from "./NeuralPathwaysControlPanel";
import NucleusControlPanel from "./NucleusControlPanel";
import ToolsControlPanel from "./ToolsControlPanel";

interface ControlPanelProps {
    breadcrumbs?: {
        label: string;
        onClick?: () => void;
        isCurrentPage?: boolean;
    }[];
}

function ControlPanelSidebar({ breadcrumbs }: ControlPanelProps) {
    const controlPanelStatus = useSelector(selectControlPanelStatus);
    const controlPanelComponent: ControlPanelComponentType = useSelector(
        selectSelectedControlPanelComponent
    );
    const [previousControlPanelStatus, setPreviousControlPanelStatus] = useState<
        NodeState["controlPanelStatus"] | undefined
    >(undefined);

    const dispatch = useDispatch();

    const onBreadCrumbLinkClick = (breadcrumb) => {
        if (typeof breadcrumb.onClick === "function") {
            breadcrumb.onClick();
        }
    };

    const navigateBack = () => {
        // eslint-disable-next-line no-undef
        window.history.back();
    };

    const toggleControlPanelStatus = () => {
        if (controlPanelStatus === "hidden") {
            dispatch(setControlPanelStatus(previousControlPanelStatus || "warning"));
        } else {
            setPreviousControlPanelStatus(controlPanelStatus);
            dispatch(setControlPanelStatus("hidden"));
        }
    };

    return (
        <div id="control-panel" className={controlPanelStatus !== "hidden" ? "expanded" : ""}>
            <div id="control-panel-header">
                <button onClick={navigateBack} className="back-btn">
                    <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        height="1em"
                        width="1em"
                        className="icon"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M21 11H6.414l5.293-5.293-1.414-1.414L2.586 12l7.707 7.707 1.414-1.414L6.414 13H21z"></path>
                    </svg>
                </button>
                <div className="flex items-center">
                    <Breadcrumb spacing="2px" separator={<ChevronRightIcon color="white" />}>
                        <BreadcrumbItem>
                            <BreadcrumbLink fontSize={"13px"}>Operating Room</BreadcrumbLink>
                        </BreadcrumbItem>

                        {breadcrumbs?.map((breadcrumb, index) => {
                            return (
                                <BreadcrumbItem key={index}>
                                    <BreadcrumbLink
                                        className="whitespace-nowrap max-w-[103px] overflow-hidden text-ellipsis"
                                        fontSize={"13px"}
                                        onClick={() => onBreadCrumbLinkClick(breadcrumb)}
                                        isCurrentPage={!!breadcrumb.isCurrentPage}
                                    >
                                        {breadcrumb.label}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                            );
                        })}
                    </Breadcrumb>
                </div>
                <button
                    id="control-panel-close-btn"
                    style={{ display: controlPanelComponent === "none" ? "none" : "block" }}
                    onClick={toggleControlPanelStatus}
                >
                    <svg
                        className={
                            "control-panel-hide-svg" +
                            (controlPanelComponent !== "none" && controlPanelStatus === "hidden"
                                ? " rotate-with-transition"
                                : "")
                        }
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 16 16"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
                        ></path>
                    </svg>
                </button>
            </div>

            <div
                id="control-panel-body"
                className={controlPanelStatus !== "hidden" ? "expanded" : ""}
            >
                <div className="panel-contents-body pb-[16px]">
                    {/* Tools control panel */}
                    {controlPanelComponent === "tools" && <ToolsControlPanel />}

                    {/* Memories control */}
                    {controlPanelComponent === "memories" && <MemoriesControlPanel />}

                    {/* Neural pathways control panel */}
                    {controlPanelComponent === "neuralPathWays" && <NeuralPathwaysControlPanel />}

                    {/* Nucleus control panel */}
                    {controlPanelComponent === "nucleus" && <NucleusControlPanel />}

                    {/* Cell encoder */}
                    {controlPanelComponent === "cellEncoder" && <CellEncoderControlPanel />}

                    {/* Agency Tool control panel */}
                    {controlPanelComponent === "agencyTools" && <AgencyToolControlPanel />}
                </div>

                <AnimatePresence>
                    {controlPanelStatus === "warning" && (
                        <motion.div
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            onClick={() => dispatch(setControlPanelStatus("normal"))}
                            className={"warning-cover panel-contents"}
                            id="warning-cover"
                        >
                            <h2 className="title text-[17px]">WARNING</h2>
                            <p className="subtitle">
                                Making any changes to the nucleus or nucleoli will
                                <br />
                                alter cellular reasoning and behaviour. It is strongly
                                <br />
                                recommended that such modifications be undertaken <br />
                                by a digital brain surgeon or an engineer with an <br />
                                in depth understanding of the NEO AI system.
                            </p>
                            <p className="subtitle">Unsure? Please call + 1 (913)-375-6840.</p>

                            <div
                                onClick={() => dispatch(setControlPanelStatus("normal"))}
                                className="proceed-btn"
                            >
                                I understand
                                <p className="active-text">PROCEED</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default ControlPanelSidebar;
